import React, { createContext, useContext, useEffect, useState } from 'react';
import { UserData } from '../types/auth';
import { getStoredToken, getStoredUserData, clearStoredAuthData, useLogin, useGoogleAuth, useTelegramAuth, useMicrosoftAuth, TelegramAuthData, LoginError } from '../services/authService';
import { useUpdateUserInfo, getUserInfo } from '../services/userService';
import { useNotifications } from '../hooks/useNotifications';
import i18n from '../i18n';

interface AuthContextType {
  // Auth state
  isAuthenticated: boolean;
  isAuthInitialized: boolean; // Track if auth state has been initialized from storage
  user: UserData | null;
  token: string | null;
  
  // Login form state
  formData: {
    loginId: string;
    loginPwd: string;
  };
  errors: {
    loginId?: string;
    loginPwd?: string;
    general?: string;
  };
  showPassword: boolean;
  
  // Login state
  isLoggingIn: boolean;
  loginError: any;
  
  // Google auth state
  isGoogleLoggingIn: boolean;
  
  // Telegram auth state
  isTelegramLoggingIn: boolean;
  
  // Microsoft auth state
  isMicrosoftLoggingIn: boolean;
  
  // User update state
  isUpdatingUser: boolean;
  updateUserError: any;
  
  // Actions
  setFormData: (data: Partial<{ loginId: string; loginPwd: string }>) => void;
  setErrors: (errors: Partial<{ loginId?: string; loginPwd?: string; general?: string }>) => void;
  togglePasswordVisibility: () => void;
  handleLogin: () => Promise<void>;
  handleGoogleLogin: (accessToken: string, referralCode?: string) => Promise<void>;
  handleTelegramLogin: (data: TelegramAuthData) => Promise<void>;
  handleMicrosoftLogin: (accessToken: string, referralCode?: string) => Promise<void>;
  logout: () => void;
  clearErrors: () => void;
  clearLoginError: () => void;
  updateUser: (userData: Partial<UserData>) => Promise<void>;
  clearUpdateUserError: () => void;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [user, setUser] = useState<UserData | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    loginId: '',
    loginPwd: '',
  });
  
  const [errors, setErrors] = useState<{
    loginId?: string;
    loginPwd?: string;
    general?: string;
  }>({});
  
  const [showPassword, setShowPassword] = useState(false);

  // TanStack Query hooks
  const loginMutation = useLogin();
  const googleAuthMutation = useGoogleAuth();
  const telegramAuthMutation = useTelegramAuth();
  const microsoftAuthMutation = useMicrosoftAuth();
  const updateUserMutation = useUpdateUserInfo();
  const { success } = useNotifications();

   // Initialize auth state from localStorage
   useEffect(() => {
    const storedToken = getStoredToken();
    const storedUser = getStoredUserData();
    if (storedToken && storedUser) {
      setIsAuthenticated(true);
      setUser(storedUser);
      setToken(storedToken);
    }
    setIsAuthInitialized(true);
  }, []);

  // Update auth context when login succeeds
  useEffect(() => {
    if (loginMutation.isSuccess && loginMutation.data) {
      const userData = loginMutation.data.user;
      const authToken = loginMutation.data.token;
      if (userData && authToken) {
        setIsAuthenticated(true);
        setUser(userData);
        setToken(authToken);
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('userData', JSON.stringify(userData));
        // Show success notification
        success(i18n.t('auth.login.messages.loginSuccess'), i18n.t('auth.login.messages.loginSuccessMessage'), 3000);
        
        // Fetch and store allUserData after successful login
        getUserInfo().then(() => {
          window.dispatchEvent(new CustomEvent('userBalanceUpdated'));
        }).catch(error => {
          console.error('Failed to fetch user info after login:', error);
        });
      }
    }
  }, [loginMutation.isSuccess, loginMutation.data, success]);

  // Update auth context when Google auth succeeds
  useEffect(() => {
    if (googleAuthMutation.isSuccess && googleAuthMutation.data) {
      const userData = googleAuthMutation.data.user;
      const authToken = googleAuthMutation.data.token;
      const isNewUser = googleAuthMutation.data.isNewUser;

      if (userData && authToken) {
        setIsAuthenticated(true);
        setUser(userData);
        setToken(authToken);
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('userData', JSON.stringify(userData));
        
        // Show success notification
        const message = isNewUser 
          ? i18n.t('auth.google.accountCreated', 'Account created with Google!')
          : i18n.t('auth.login.messages.loginSuccess');
        success(message, '', 3000);
        
        // Fetch and store allUserData after successful login
        getUserInfo().then(() => {
          window.dispatchEvent(new CustomEvent('userBalanceUpdated'));
        }).catch(error => {
          console.error('Failed to fetch user info after Google login:', error);
        });
      }
    }
  }, [googleAuthMutation.isSuccess, googleAuthMutation.data, success]);

  // Update auth context when Telegram auth succeeds
  useEffect(() => {
    if (telegramAuthMutation.isSuccess && telegramAuthMutation.data) {
      const userData = telegramAuthMutation.data.user;
      const authToken = telegramAuthMutation.data.token;
      const isNewUser = telegramAuthMutation.data.isNewUser;
      
      if (userData && authToken) {
        setIsAuthenticated(true);
        setUser(userData);
        setToken(authToken);
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('userData', JSON.stringify(userData));
        
        // Show success notification
        const message = isNewUser 
          ? i18n.t('auth.telegram.accountCreated', 'Account created with Telegram!')
          : i18n.t('auth.login.messages.loginSuccess');
        success(message, '', 3000);
        
        // Fetch and store allUserData after successful login
        getUserInfo().then(() => {
          window.dispatchEvent(new CustomEvent('userBalanceUpdated'));
        }).catch(error => {
          console.error('Failed to fetch user info after Telegram login:', error);
        });
      }
    }
  }, [telegramAuthMutation.isSuccess, telegramAuthMutation.data, success]);

  // Update auth context when Microsoft auth succeeds
  useEffect(() => {
    if (microsoftAuthMutation.isSuccess && microsoftAuthMutation.data) {
      const userData = microsoftAuthMutation.data.user;
      const authToken = microsoftAuthMutation.data.token;
      const isNewUser = microsoftAuthMutation.data.isNewUser;
      
      if (userData && authToken) {
        setIsAuthenticated(true);
        setUser(userData);
        setToken(authToken);
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('userData', JSON.stringify(userData));
        
        // Show success notification
        const message = isNewUser 
          ? i18n.t('auth.microsoft.accountCreated', 'Account created with Microsoft!')
          : i18n.t('auth.login.messages.loginSuccess');
        success(message, '', 3000);
        
        // Fetch and store allUserData after successful login
        getUserInfo().then(() => {
          window.dispatchEvent(new CustomEvent('userBalanceUpdated'));
        }).catch(error => {
          console.error('Failed to fetch user info after Microsoft login:', error);
        });
      }
    }
  }, [microsoftAuthMutation.isSuccess, microsoftAuthMutation.data, success]);

  // Helper function to translate backend error messages
  const translateBackendError = (errorMessage: string): string => {
    const errorLower = errorMessage.toLowerCase();
    const t = i18n.t.bind(i18n);
    
    // Map common backend error messages to translation keys
    if (errorLower.includes('credential') || errorLower.includes('password') || errorLower.includes('login') || errorLower.includes('id')) {
      if (errorLower.includes('invalid') || errorLower.includes('incorrect') || errorLower.includes('wrong')) {
        return t('auth.login.validation.invalidCredentials');
      }
    }
    
    // If no mapping found, return original message
    return errorMessage;
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    const t = i18n.t.bind(i18n);
    
    // Login ID validation
    if (!formData.loginId.trim()) {
      newErrors.loginId = t('auth.login.validation.idRequired');
    } else if (formData.loginId.length < 6) {
      newErrors.loginId = t('auth.login.validation.idMinLength');
    } else if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/.test(formData.loginId)) {
      newErrors.loginId = t('auth.login.validation.idFormat');
    }
    
    // Password validation
    if (!formData.loginPwd) {
      newErrors.loginPwd = t('auth.login.validation.passwordRequired');
    } else if (formData.loginPwd.length < 6) {
      newErrors.loginPwd = t('auth.login.validation.passwordMinLength');
    } else if (!/^(?=.*[A-Za-z])(?=.*\d)/.test(formData.loginPwd)) {
      newErrors.loginPwd = t('auth.login.validation.passwordFormat');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle login submission
  const handleLogin = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }
    
    try {
      await loginMutation.mutateAsync({
        loginId: formData.loginId,
        loginPwd: formData.loginPwd,
      });
      
      // Clear form on success
      setFormData({ loginId: '', loginPwd: '' });
      setErrors({});
      
    } catch (error) {
      // Handle specific error cases - only show field errors when they are actually invalid
      const validationErrors: typeof errors = {};
      const t = i18n.t.bind(i18n);
      
      // Re-run validation to ensure any client-side issues are shown
      if (!formData.loginId.trim()) {
        validationErrors.loginId = t('auth.login.validation.idRequired');
      } else if (formData.loginId.length < 6) {
        validationErrors.loginId = t('auth.login.validation.idMinLength');
      } else if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/.test(formData.loginId)) {
        validationErrors.loginId = t('auth.login.validation.idFormat');
      }
      
      if (!formData.loginPwd) {
        validationErrors.loginPwd = t('auth.login.validation.passwordRequired');
      } else if (formData.loginPwd.length < 6) {
        validationErrors.loginPwd = t('auth.login.validation.passwordMinLength');
      } else if (!/^(?=.*[A-Za-z])(?=.*\d)/.test(formData.loginPwd)) {
        validationErrors.loginPwd = t('auth.login.validation.passwordFormat');
      }
      
      const hasValidationErrors = Object.keys(validationErrors).length > 0;

      if (hasValidationErrors) {
        setErrors(validationErrors);
      } else {
        // Password lockout: use i18n for remaining attempts or suspended message
        if (error instanceof LoginError) {
          if (error.accountSuspended) {
            setErrors({
              general: t('auth.login.validation.accountSuspended', {
                defaultValue: 'Account suspended due to too many failed login attempts. Please contact support.',
              }),
            });
          } else if (typeof error.remainingAttempts === 'number') {
            setErrors({
              general: t('auth.login.validation.wrongPasswordRemaining', {
                count: error.remainingAttempts,
                max: 5,
                defaultValue: 'Wrong password. {{count}} attempt(s) remaining. If you fail {{max}} times, your account will be suspended.',
              })
            });
          } else {
            setErrors({ general: error.message || t('auth.login.validation.invalidCredentials') });
          }
          return;
        }

        // Get error message from various error formats
        let errorMessage = '';
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null) {
          errorMessage = (error as any).message || (error as any).response?.data?.message?.warning || '';
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
        
        // Handle network errors
        if (!errorMessage || errorMessage === '[object Event]' || errorMessage.includes('Network')) {
          setErrors({ general: 'Network error. Please check if the server is running.' });
        } else {
          const translatedError = translateBackendError(errorMessage);
          if (
            errorMessage.toLowerCase().includes('credential') ||
            errorMessage.toLowerCase().includes('password') ||
            errorMessage.toLowerCase().includes('login') ||
            errorMessage.toLowerCase().includes('id')
          ) {
            setErrors({ general: translatedError || t('auth.login.validation.invalidCredentials') });
          } else {
            setErrors({ general: translatedError || t('auth.login.validation.loginFailed') });
          }
        }
      }
    }
  };

  // Handle Google login
  const handleGoogleLogin = async (accessToken: string, referralCode?: string): Promise<void> => {
    try {
      await googleAuthMutation.mutateAsync({
        accessToken,
        referralCode,
      });
      setErrors({});
    } catch (error) {
      let errorMessage = 'Google authentication failed';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        errorMessage = (error as any).message || (error as any).response?.data?.message?.warning || errorMessage;
      }
      setErrors({ general: errorMessage });
    }
  };

  // Handle Telegram login
  const handleTelegramLogin = async (data: TelegramAuthData): Promise<void> => {
    try {
      await telegramAuthMutation.mutateAsync(data);
      setErrors({});
    } catch (error) {
      let errorMessage = 'Telegram authentication failed';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        errorMessage = (error as any).message || (error as any).response?.data?.message?.warning || errorMessage;
      }
      setErrors({ general: errorMessage });
    }
  };

  // Handle Microsoft login
  const handleMicrosoftLogin = async (accessToken: string, referralCode?: string): Promise<void> => {
    try {
      await microsoftAuthMutation.mutateAsync({ accessToken, referralCode });
      setErrors({});
    } catch (error) {
      let errorMessage = 'Microsoft authentication failed';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        errorMessage = (error as any).message || (error as any).response?.data?.message?.warning || errorMessage;
      }
      setErrors({ general: errorMessage });
    }
  };

  // Logout function
  const logout = () => {
    clearStoredAuthData();
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    setFormData({ loginId: '', loginPwd: '' });
    setErrors({});
    
    // Show success notification
    success(i18n.t('auth.login.messages.logoutSuccess'), i18n.t('auth.login.messages.logoutSuccessMessage'), 3000);
  };

  // Utility functions
  const updateFormData = (data: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...data }));
    // Clear errors when user starts typing
    const fieldName = Object.keys(data)[0] as keyof typeof errors;
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: undefined }));
    }
  };
  
  const updateErrors = (newErrors: Partial<typeof errors>) => {
    setErrors(prev => ({ ...prev, ...newErrors }));
  };
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  const clearErrors = () => {
    setErrors({});
  };
  
  const clearLoginError = () => {
    loginMutation.reset();
  };

  const updateUser = async (userData: Partial<UserData>): Promise<void> => {
    if (!user) {
      throw new Error('No user logged in');
    }

    try {
      // Map UserData fields to UserUpdateRequest fields
      const updateData = {
        name: userData.name,
        email: userData.email,
      };

      // Remove undefined values
      const filteredData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      );

      await updateUserMutation.mutateAsync(filteredData);
      
      // Update local state and storage on success
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
    } catch (error) {
      throw error;
    }
  };

  const clearUpdateUserError = () => {
    updateUserMutation.reset();
  };

  const refreshUserData = async (): Promise<void> => {
    if (!isAuthenticated) {
      throw new Error('User not authenticated');
    }

    try {
      const response = await getUserInfo();
      // Handle both response structures: response.user or response.data.user
      const apiUser = (response as any)?.user || response?.data?.user;
      if (apiUser) {
        // Map API UserData to Auth UserData format
        // Handle balance - could be a number or an object
        const balanceMoney = typeof apiUser.balance === 'number' 
          ? apiUser.balance 
          : (apiUser.balance?.money || apiUser.balance || 0);
        const balanceRolling = typeof apiUser.balance === 'object' 
          ? (apiUser.balance?.rolling || 0)
          : 0;
        const balanceBettingMoney = typeof apiUser.balance === 'object' 
          ? (apiUser.balance?.bettingMoney || 0)
          : 0;

        const authUser: UserData = {
          id: apiUser.id,
          name: apiUser.name,
          email: apiUser.email,
          level: apiUser.level,
          balance: {
            money: balanceMoney,
            rolling: balanceRolling,
            bettingMoney: balanceBettingMoney
          }
        };
        setUser(authUser);
        localStorage.setItem('userData', JSON.stringify(authUser));
        
        // Dispatch event to notify Header and other components of balance update
        window.dispatchEvent(new CustomEvent('userBalanceUpdated'));
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    // Auth state
    isAuthenticated,
    isAuthInitialized,
    user,
    token,
    
    // Form state
    formData,
    errors,
    showPassword,
    
    // Login state
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    
    // Google auth state
    isGoogleLoggingIn: googleAuthMutation.isPending,
    
    // Telegram auth state
    isTelegramLoggingIn: telegramAuthMutation.isPending,
    
    // Microsoft auth state
    isMicrosoftLoggingIn: microsoftAuthMutation.isPending,
    
    // User update state
    isUpdatingUser: updateUserMutation.isPending,
    updateUserError: updateUserMutation.error,
    
    // Actions
    setFormData: updateFormData,
    setErrors: updateErrors,
    togglePasswordVisibility,
    handleLogin,
    handleGoogleLogin,
    handleTelegramLogin,
    handleMicrosoftLogin,
    logout,
    clearErrors,
    clearLoginError,
    updateUser,
    clearUpdateUserError,
    refreshUserData,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 